import React, { FC, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useKeyboardState } from 'hooks/use-keyboard-state';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useRedeemPromoCode } from 'state/loyalty/hooks/use-redeem-promo-codes';
import { routes } from 'utils/routing';

import {
  Container,
  StyledActionSheet,
  StyledButton,
  StyledInput,
} from './loyalty-promo-code-drawer.styled';
import { ILoyaltyPromoCodeDrawerProps, PromoCodeError } from './types';
import { buildErrorMessageFromPromoCodeError, createRbiApplyPromoCodeEvent } from './utils';

export const LoyaltyPromoCodeDrawer: FC<ILoyaltyPromoCodeDrawerProps> = ({
  onDismiss,
  promoCode,
  dialogState,
  setPromoCodeRef,
  onPromoCodeRedeemSuccess,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { keyboardHeight } = useKeyboardState();
  const { formatMessage } = useIntl();
  const [inputValue, setInputValue] = useState(promoCode ?? '');
  const { loyaltyUser } = useLoyaltyUser();
  const { user } = useAuthContext();
  const { trackEvent } = useCdpContext();
  const [redeemMutation, { loading, error }] = useRedeemPromoCode();
  const { isAuthenticated, setOriginLocation } = useAuthContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);

  let loyaltyId = loyaltyUser?.id;

  // Some markets want to use promo-code and have enabled loyalty flag only on the BE.
  // This logic permits use promo code, even when the loyalty flag is not enabled on the FE
  // When the loyalty flag is enabled on the BE, we can get the loyaltyId from the user data
  // instead of the loyaltyUser, which is only set when the loyalty FE flag is enabled
  if (enableLoyaltyPromoCodes) {
    loyaltyId = user?.loyaltyId ?? loyaltyUser?.id;
  }

  const handleOnApplyPromoCode = useCallback(
    async (promoCodeValue: string) => {
      if (!isAuthenticated() || !loyaltyId) {
        const promoCodeParam = promoCodeValue ? `?promoCode=${promoCodeValue}` : '';
        setPromoCodeRef(inputValue);
        setOriginLocation(`${pathname}${promoCodeParam}`);
        return navigate(routes.signUp, { replace: true });
      }
      let event = createRbiApplyPromoCodeEvent(promoCodeValue, 'Successful');
      const personalizedOffer = await redeemMutation(loyaltyId, promoCodeValue).catch(
        (e: PromoCodeError) => {
          event = createRbiApplyPromoCodeEvent(promoCodeValue, 'Failed', e.message);
        }
      );
      trackEvent(event);

      if (personalizedOffer) {
        onPromoCodeRedeemSuccess?.(personalizedOffer);
        setInputValue('');
        dialogState.hide();
        onDismiss();
        searchParams.delete('promoCode');
        setSearchParams(searchParams, { replace: true });
      }
    },
    [
      isAuthenticated,
      loyaltyId,
      redeemMutation,
      trackEvent,
      setPromoCodeRef,
      inputValue,
      setOriginLocation,
      pathname,
      navigate,
      onPromoCodeRedeemSuccess,
      dialogState,
      onDismiss,
      searchParams,
      setSearchParams,
    ]
  );

  const onChangeInput = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(value);
  };

  return (
    <StyledActionSheet
      dialogState={dialogState}
      onOpenEventMessage={formatMessage({ id: 'applyPromoCode' })}
      header={formatMessage({ id: 'applyPromoCode' })}
      aria-label={formatMessage({ id: 'applyPromoCode' })}
    >
      <Container
        $keyboardHeight={keyboardHeight}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleOnApplyPromoCode(inputValue);
          }
        }}
      >
        <StyledInput
          value={inputValue}
          onChange={onChangeInput}
          label={formatMessage({ id: 'enterPromoCode' })}
          errorMessage={
            error ? buildErrorMessageFromPromoCodeError(error, formatMessage) : undefined
          }
          autoFocus
        />
        <StyledButton
          onClick={() => {
            handleOnApplyPromoCode(inputValue);
          }}
          disabled={!inputValue}
          isLoading={loading}
        >
          {formatMessage({ id: 'apply' })}
        </StyledButton>
      </Container>
    </StyledActionSheet>
  );
};
